import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { NOTIFICATIONS_REFRESH_EVENT } from '@/hooks/useUnreadNotifications';
import { toast } from '@/hooks/use-toast';

/**
 * Native push handler. CRITICAL: we DO NOT schedule a LocalNotification on
 * `pushNotificationReceived`. Modern Android/FCM already renders the system
 * tray banner for the incoming push (with our branded icon, color, and logo
 * applied by `process-notifications`). Scheduling a second LocalNotification
 * here produced the duplicate generic blue-info copy seen in the user's
 * notification shade.
 *
 * Behavior now:
 *   - Foreground receive  -> refresh inbox/unread; show an in-app toast only.
 *                            FCM itself shows the heads-up banner; we never
 *                            create a parallel system-tray entry.
 *   - Background/killed   -> system tray handles it via FCM. Nothing to do.
 *   - Tap action          -> deep-link to /customer/booking/:id when the
 *                            payload has a booking_id, otherwise the inbox.
 */
export function usePushNotificationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Resolve where a notification payload should deep-link to.
    // Returns a same-origin path or null if no deep-link applies.
    const resolveDeepLink = (data: Record<string, string>): string | null => {
      const eventType = data.event_type || data.event || "";
      const wantsScratch =
        eventType === "scratch_card_revealed" || data.open_scratch === "1";

      if (wantsScratch && data.booking_id) {
        // Always normalise to the in-app route; the admin's /ride/* path
        // is only an alias and we don't want refresh loops via Navigate.
        return `/customer/booking/${data.booking_id}?openScratch=1`;
      }

      // Use deep_link verbatim if it's a safe relative path.
      if (typeof data.deep_link === "string" && data.deep_link.startsWith("/")) {
        // Rewrite /ride/<id>... to /customer/booking/<id>... so the route exists.
        const rideMatch = data.deep_link.match(/^\/ride\/([^/?#]+)(.*)$/);
        if (rideMatch) {
          return `/customer/booking/${rideMatch[1]}${rideMatch[2] || ""}`;
        }
        return data.deep_link;
      }

      if (data.booking_id) {
        return `/customer/booking/${data.booking_id}`;
      }
      return null;
    };

    const recvSub = PushNotifications.addListener(
      'pushNotificationReceived',
      (n) => {
        // Always refresh the in-app inbox / unread badge.
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));

        const data = (n.data ?? {}) as Record<string, string>;
        const title = n.title || data.title || 'Notification';
        const body = n.body || data.body || '';

        // In-app toast for foreground feedback only. This does NOT add a
        // system-tray notification — FCM already handles the tray banner.
        try {
          toast({ title, description: body });
        } catch {
          /* toast unavailable — silently ignore */
        }
      },
    );

    const tapSub = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        const data = (action.notification.data ?? {}) as Record<string, string>;
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));
        const target = resolveDeepLink(data);
        navigate(target ?? '/customer/notifications');
      },
    );

    return () => {
      recvSub.then((s) => s.remove());
      tapSub.then((s) => s.remove());
    };
  }, [navigate]);
}
